import type { DataGridGroupBucketViewState } from './data-grid.types';

/** Sort/group path used by the ungrouped (flat) table. */
export const DATA_GRID_FLAT_SORT_PATH: string = '__flat__';

/** Thead drop-list id for the ungrouped table. */
export const DATA_GRID_FLAT_THEAD_ID: string = 'thead-flat';

/** Fixed row height in pixels — required for CDK FixedSizeVirtualScroll. */
export const DATA_GRID_ROW_HEIGHT: number = 37;

/** Sticky group-header height in pixels; leaf theads offset by `(depth + 1) * this`. */
export const DATA_GRID_GROUP_HEADER_HEIGHT: number = 28;

/** Groups with fewer rows than this threshold render without virtual scrolling. */
export const DATA_GRID_VIRTUALIZATION_THRESHOLD: number = 100;

/** Maximum visible height ratio for a grouped leaf virtual scroll viewport. */
export const DATA_GRID_LEAF_VIEWPORT_MAX_HEIGHT_RATIO: number = 0.6;

/**
 * Number of rows before the end of loaded data at which the next page fetch
 * is triggered.
 */
export const DATA_GRID_LOAD_MORE_BUFFER: number = 20;

/**
 * Fallback viewport height in pixels when `window` is unavailable (SSR / tests).
 */
const FALLBACK_VIEWPORT_HEIGHT_PX: number = 480;

/**
 * Returns the window inner height, or a desktop-sized fallback.
 *
 * @returns Viewport height in pixels.
 */
export function resolveWindowInnerHeight(): number {
  return typeof window !== 'undefined' ? window.innerHeight : FALLBACK_VIEWPORT_HEIGHT_PX;
}

/**
 * Calculates the visible viewport height for a leaf group's virtual scroll container.
 *
 * @param rowCount - Number of loaded rows in the leaf.
 * @param rowHeight - Fixed row height in pixels.
 * @param maxHeightRatio - Cap as a fraction of the window height.
 * @param windowHeightPx - Window inner height used for the cap.
 * @returns Height in pixels, at least one row tall.
 */
export function getLeafViewportVisibleHeight(
  rowCount: number,
  rowHeight: number = DATA_GRID_ROW_HEIGHT,
  maxHeightRatio: number = DATA_GRID_LEAF_VIEWPORT_MAX_HEIGHT_RATIO,
  windowHeightPx: number = resolveWindowInnerHeight()
): number {
  if (rowCount <= 0) {
    return rowHeight;
  }
  const contentHeight: number = rowCount * rowHeight;
  const maxHeightPx: number = windowHeightPx * maxHeightRatio;
  return Math.min(contentHeight, maxHeightPx);
}

/**
 * Returns true when the given bucket path still has server pages to fetch.
 *
 * @param path - Composite group path, or {@link DATA_GRID_FLAT_SORT_PATH}.
 * @param rowCount - Currently loaded row count.
 * @param totalCount - Server-reported total for the bucket.
 * @param bucketStates - Per-path loading / hasNextPage flags.
 * @returns Whether more pages exist.
 */
export function groupHasMorePages(
  path: string,
  rowCount: number,
  totalCount: number,
  bucketStates: Record<string, DataGridGroupBucketViewState>
): boolean {
  const bucket: DataGridGroupBucketViewState | undefined = bucketStates[path];
  if (bucket !== undefined) {
    return bucket.hasNextPage;
  }
  return rowCount < totalCount;
}

/**
 * True when a bucket's rows are currently being fetched.
 *
 * @param path - Composite group path.
 * @param bucketStates - Per-path loading / hasNextPage flags.
 * @returns Whether the bucket is loading.
 */
export function isGroupBucketLoading(
  path: string,
  bucketStates: Record<string, DataGridGroupBucketViewState>
): boolean {
  return bucketStates[path]?.loading === true;
}

/**
 * Whether a table body should use CDK virtual scroll.
 *
 * @param rowCount - Currently loaded row count.
 * @param sortPath - Flat path or composite group path.
 * @param virtualizationThreshold - Minimum loaded rows that always virtualize.
 * @param hasMore - Whether the flat view has more server pages.
 * @param bucketHasMorePages - Whether this grouped leaf has more pages.
 * @returns Whether to render a virtual viewport.
 */
export function shouldVirtualizeGroupRows(
  rowCount: number,
  sortPath: string,
  virtualizationThreshold: number = DATA_GRID_VIRTUALIZATION_THRESHOLD,
  hasMore: boolean = false,
  bucketHasMorePages: boolean = false
): boolean {
  if (sortPath === DATA_GRID_FLAT_SORT_PATH) {
    return rowCount >= virtualizationThreshold || hasMore;
  }
  if (rowCount === 0) {
    return false;
  }
  if (rowCount >= virtualizationThreshold) {
    return true;
  }
  // A leaf requests its next page from the virtual viewport's scroll events, so a bucket with
  // pages left has to virtualize whatever its size. Gating this on the total being large enough
  // to virtualize on its own stranded every bucket between the batch page size and the threshold:
  // 50 rows arrive, nothing renders a viewport, and the remaining rows can never be asked for.
  return bucketHasMorePages;
}

/**
 * True when an expanded leaf group has no matching rows because filters excluded every item.
 *
 * @param rowCount - Currently loaded row count.
 * @param totalCount - Server-reported total for the bucket.
 * @param sortPath - Flat path or composite group path.
 * @param hasActiveColumnFilters - Whether any column filter is constraining results.
 * @param isBucketLoading - Whether this leaf is still fetching rows.
 * @returns Whether to show the filtered-empty message instead of a table.
 */
export function shouldShowFilteredGroupEmptyState(
  rowCount: number,
  totalCount: number,
  sortPath: string,
  hasActiveColumnFilters: boolean,
  isBucketLoading: boolean
): boolean {
  if (rowCount > 0 || totalCount > 0) {
    return false;
  }
  if (!hasActiveColumnFilters) {
    return false;
  }
  if (sortPath !== DATA_GRID_FLAT_SORT_PATH && isBucketLoading) {
    return false;
  }
  return true;
}

/**
 * Whether a virtual-scroll index change should request the next page.
 *
 * @param hasMore - Whether more server pages exist for this viewport.
 * @param loading - Whether a primary fetch is in flight.
 * @param loadingMore - Whether a pagination request is already in flight.
 * @param index - First visible item index.
 * @param viewportRows - Estimated visible row count in the viewport.
 * @param rowCount - Currently loaded row count.
 * @param buffer - Rows-before-end that trigger a fetch.
 * @returns Whether to emit `loadMore`.
 */
export function shouldLoadMoreOnScroll(
  hasMore: boolean,
  loading: boolean,
  loadingMore: boolean,
  index: number,
  viewportRows: number,
  rowCount: number,
  buffer: number = DATA_GRID_LOAD_MORE_BUFFER
): boolean {
  if (!hasMore || loading || loadingMore) {
    return false;
  }
  return index + viewportRows + buffer >= rowCount;
}

/**
 * Resolves the `loadMore` payload: composite path in per-group mode, otherwise null.
 *
 * @param sortPath - Flat path or composite group path.
 * @param primaryGroupField - Outermost group field, or null in flat mode.
 * @returns Path to load, or null for flat/generic pagination.
 */
export function resolveLoadMoreValue(sortPath: string, primaryGroupField: string | null): string | null {
  if (primaryGroupField !== null && sortPath !== DATA_GRID_FLAT_SORT_PATH) {
    return sortPath;
  }
  return null;
}
